import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ChatCircle, SpeakerHigh, SpeakerSlash, BellRinging, Check, X, Gear } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 20000;
const LS_SOUND_ON = 'chat_bell_sound_on';

// Persistent unlocked AudioContext — created lazily on first user click so it survives Chrome's autoplay policy.
let _audioCtx = null;
const ensureAudio = async () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!_audioCtx) _audioCtx = new AC();
    // 'suspended' state means autoplay-policy is still blocking — needs a user-gesture-driven resume()
    if (_audioCtx.state === 'suspended') await _audioCtx.resume();
    return _audioCtx;
  } catch (_) {
    return null;
  }
};

const playPing = async () => {
  const ctx = await ensureAudio();
  if (!ctx || ctx.state !== 'running') return false;
  const now = ctx.currentTime;
  [880, 1320].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, now + i * 0.08);
    gain.gain.exponentialRampToValueAtTime(0.22, now + i * 0.08 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.08 + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + i * 0.08);
    osc.stop(now + i * 0.08 + 0.4);
  });
  return true;
};

const ChatBell = () => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [lastTotal, setLastTotal] = useState(0);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
  );
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem(LS_SOUND_ON) !== 'false'; } catch { return true; }
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const initialised = useRef(false);
  const settingsRef = useRef(null);

  const toggleSound = async () => {
    const next = !soundOn;
    setSoundOn(next);
    try { localStorage.setItem(LS_SOUND_ON, String(next)); } catch { /* ignore */ }
    if (next) {
      // Unlock AudioContext + play sample ping on user-initiated click (satisfies Chrome's autoplay rule)
      const ok = await playPing();
      if (!ok) toast.error('Your browser blocked audio. Try clicking the speaker icon again — must be a real click.');
    }
  };

  const askNotificationPermission = async () => {
    if (typeof Notification === 'undefined') {
      toast.error('Desktop notifications are not supported in this browser');
      return;
    }
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === 'granted') {
        // Fire a confirmation notification so the user can SEE that it works
        new Notification('PayrollHub notifications are ON ✅', {
          body: "You'll now see desktop alerts for new chat messages.",
          icon: '/favicon.ico',
          tag: 'payrollhub-chat-test',
        });
        toast.success('Desktop notifications enabled');
      } else if (p === 'denied') {
        toast.error("Permission denied. To re-enable: click the 🔒 icon in your browser's address bar → Site Settings → Notifications → Allow.");
      }
    } catch (e) {
      toast.error('Could not request permission: ' + e.message);
    }
  };

  const testSound = async () => {
    const ok = await playPing();
    if (!ok) toast.error("Audio still blocked — make sure the speaker icon is ON. If you don't hear anything, check your system volume.");
    else toast.success('Played test ping');
  };

  const testNotification = () => {
    if (Notification.permission !== 'granted') {
      toast.error('Allow notifications first using the button above');
      return;
    }
    new Notification('Test — New chat message', {
      body: 'This is what an incoming chat message looks like.',
      icon: '/favicon.ico',
      tag: 'payrollhub-chat-test',
    });
  };

  // Ask once after login — quick prompt
  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    if (permission === 'default') {
      const t = setTimeout(() => {
        Notification.requestPermission().then((p) => setPermission(p)).catch(() => {});
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [permission]);

  // Close settings popover on click-outside
  useEffect(() => {
    if (!settingsOpen) return;
    const onClick = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setSettingsOpen(false);
    };
    setTimeout(() => window.addEventListener('mousedown', onClick), 0);
    return () => window.removeEventListener('mousedown', onClick);
  }, [settingsOpen]);

  const showDesktopNotification = (delta) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    try {
      const n = new Notification(`${delta} new chat message${delta > 1 ? 's' : ''}`, {
        body: 'Click to open PayrollHub Chat',
        icon: '/favicon.ico',
        tag: 'payrollhub-chat',
        renotify: true,
      });
      n.onclick = () => {
        window.focus();
        window.location.href = '/chat';
        n.close();
      };
    } catch (_) { /* throttled */ }
  };

  const load = async (notify = true) => {
    try {
      const r = await axios.get(`${API_URL}/api/chat/unread-count`, { withCredentials: true });
      const total = Number(r.data?.total || 0);
      setCount(total);
      if (notify && initialised.current && total > lastTotal) {
        const delta = total - lastTotal;
        if (!window.location.pathname.startsWith('/chat')) {
          showDesktopNotification(delta);
          if (soundOn) playPing();
        }
      }
      setLastTotal(total);
      initialised.current = true;
    } catch (_) { /* swallow */ }
  };

  useEffect(() => {
    if (!user) return;
    load(false);
    const t = setInterval(() => load(true), POLL_MS);
    const onFocus = () => load(true);
    const onRead = () => load(false);
    window.addEventListener('focus', onFocus);
    window.addEventListener('chat-read', onRead);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('chat-read', onRead);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?._id, soundOn]);

  const permPill = (() => {
    if (permission === 'granted') return { label: 'Allowed', bg: '#dcfce7', fg: '#15803d' };
    if (permission === 'denied') return { label: 'Blocked', bg: '#fee2e2', fg: '#b91c1c' };
    return { label: 'Not asked', bg: '#fef3c7', fg: '#92400e' };
  })();

  return (
    <div className="relative flex items-center gap-0.5">
      <button
        onClick={() => setSettingsOpen((o) => !o)}
        className="relative inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 transition-colors"
        title="Chat notification settings"
        data-testid="topbar-chat-settings"
      >
        <Gear size={14} style={{ color: '#94a3b8' }} />
      </button>
      <button
        onClick={toggleSound}
        className="relative inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 transition-colors"
        title={soundOn ? 'Sound is ON — click to mute' : 'Sound is OFF — click to enable'}
        data-testid="topbar-chat-sound-toggle"
      >
        {soundOn
          ? <SpeakerHigh size={14} style={{ color: 'var(--dx-text2)' }} />
          : <SpeakerSlash size={14} style={{ color: '#94a3b8' }} />}
      </button>
      <Link
        to="/chat"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-100 transition-colors"
        data-testid="topbar-chat-bell"
        title={count > 0 ? `${count} unread chat message${count > 1 ? 's' : ''}` : 'Chat'}
      >
        <ChatCircle size={18} style={{ color: 'var(--dx-text2)' }} />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white"
            style={{ background: '#dc2626' }}
            data-testid="topbar-chat-count"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Link>

      {/* Settings popover */}
      {settingsOpen && (
        <div
          ref={settingsRef}
          className="absolute top-10 right-0 w-72 bg-white border rounded-lg shadow-xl z-50 p-3"
          data-testid="chat-settings-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-700">Chat notifications</div>
            <button onClick={() => setSettingsOpen(false)} className="text-gray-400 hover:text-gray-700"><X size={12} /></button>
          </div>

          {/* Bell sound */}
          <div className="border-b pb-2 mb-2">
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-semibold flex items-center gap-1">
                {soundOn ? <SpeakerHigh size={14} /> : <SpeakerSlash size={14} />} Bell sound
              </span>
              <button
                onClick={toggleSound}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${soundOn ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}
                data-testid="chat-settings-sound-toggle"
              >
                {soundOn ? 'ON' : 'OFF'}
              </button>
            </div>
            <button
              onClick={testSound}
              className="text-[11px] text-emerald-700 underline mt-1"
              data-testid="chat-settings-test-sound"
            >
              ▸ Test sound
            </button>
          </div>

          {/* Browser notifications */}
          <div>
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-semibold flex items-center gap-1">
                <BellRinging size={14} /> Desktop alerts
              </span>
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: permPill.bg, color: permPill.fg }}
                data-testid="chat-settings-permission-pill"
              >
                {permPill.label}
              </span>
            </div>
            {permission === 'granted' ? (
              <button
                onClick={testNotification}
                className="text-[11px] text-emerald-700 underline mt-1"
                data-testid="chat-settings-test-notification"
              >
                ▸ Send test notification
              </button>
            ) : permission === 'denied' ? (
              <div className="text-[11px] text-gray-600 mt-1 leading-relaxed">
                Your browser is blocking notifications. To re-enable: click the <span className="font-bold">🔒 lock icon</span> in your address bar → <span className="font-bold">Site settings</span> → <span className="font-bold">Notifications: Allow</span>, then refresh.
              </div>
            ) : (
              <button
                onClick={askNotificationPermission}
                className="text-[11px] text-white bg-emerald-600 hover:bg-emerald-700 px-2 py-1 rounded mt-1"
                data-testid="chat-settings-ask-permission"
              >
                Enable desktop alerts
              </button>
            )}
          </div>

          <div className="text-[10px] text-gray-400 mt-3 pt-2 border-t">
            Settings saved to this device. Each user/browser controls their own.
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBell;
